const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

class CVService {
  // Get auth token from localStorage
  getAuthToken() {
    return localStorage.getItem('token');
  }

  // Build headers with auth token
  getHeaders() {
    const token = this.getAuthToken();
    return {
      'Authorization': token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json'
    };
  }

  // Get all CVs with optional filtering
  async getAllCVs(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.tags) queryParams.append('tags', Array.isArray(filters.tags) ? filters.tags.join(',') : filters.tags);
      if (filters.archived !== undefined) queryParams.append('archived', filters.archived.toString());
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);

      const response = await fetch(`${API_BASE_URL}/api/cv?${queryParams}`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CVs: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching CVs:', error);
      throw error;
    }
  }

  // Get CVs grouped by category
  async getCVsByCategory() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cv/by-category`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CVs by category: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching CVs by category:', error);
      throw error;
    }
  }

  // Get CV analytics
  async getCVAnalytics() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cv/analytics`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CV analytics: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching CV analytics:', error);
      throw error;
    }
  }

  // Get CV by ID
  async getCVById(cvId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cv/${cvId}`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CV: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching CV by ID:', error);
      throw error;
    }
  }

  // Upload new CV
  async uploadCV(file, cvData) {
    try {
      const formData = new FormData();
      formData.append('cv', file);
      
      if (cvData.tags && cvData.tags.length > 0) {
        const tagsArray = Array.isArray(cvData.tags) ? cvData.tags : cvData.tags.split(',');
        const cleanTags = tagsArray.map(tag => tag.trim()).filter(tag => tag.length > 0);
        if (cleanTags.length > 0) {
          formData.append('tags', cleanTags.join(','));
        }
      }

      const token = this.getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/cv/upload`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
          // Don't set Content-Type, let browser set it with boundary for FormData
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to upload CV: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading CV:', error);
      throw error;
    }
  }

  // Manually trigger CV analysis
  async analyzeCV(cvId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cv/${cvId}/analyze`, {
        method: 'POST',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to analyze CV: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error analyzing CV:', error);
      throw error;
    }
  }

  // Get CV recommendations for a job
  async getCVRecommendations(jobId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cv/recommendations/${jobId}`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to get CV recommendations: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting CV recommendations:', error);
      throw error;
    }
  }

  // Associate CV with a job
  async associateWithJob(cvId, jobId, outcome = 'pending') {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cv/${cvId}/jobs/${jobId}`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ outcome })
      });

      if (!response.ok) {
        throw new Error(`Failed to associate CV with job: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error associating CV with job:', error);
      throw error;
    }
  }

  // Update job outcome for a CV
  async updateJobOutcome(cvId, jobId, outcome) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cv/${cvId}/jobs/${jobId}/outcome`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify({ outcome })
      });

      if (!response.ok) {
        throw new Error(`Failed to update job outcome: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating job outcome:', error);
      throw error;
    }
  }

  // Update CV details
  async updateCV(cvId, updateData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cv/${cvId}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error(`Failed to update CV: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating CV:', error);
      throw error;
    }
  }

  // Delete CV
  async deleteCV(cvId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cv/${cvId}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to delete CV: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting CV:', error);
      throw error;
    }
  }

  // Download CV file
  async downloadCV(cvId, filename) {
    try {
      const token = this.getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/cv/${cvId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to download CV: ${response.statusText}`);
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { success: true, message: 'File downloaded successfully' };
    } catch (error) {
      console.error('Error downloading CV:', error);
      throw error;
    }
  }

  // Get CV viewing URL for PDF preview
  async getCVViewingURL(cvId) {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      console.log('Fetching PDF from:', `${API_BASE_URL}/api/cv/${cvId}/view`);
      
              let response = await fetch(`${API_BASE_URL}/api/cv/${cvId}/view`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // If view endpoint doesn't exist (404), fallback to download endpoint
      if (response.status === 404) {
        console.log('View endpoint not found, trying download endpoint...');
        response = await fetch(`${API_BASE_URL}/api/cv/${cvId}/download`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If it's not JSON, use the status text
        }
        
        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      console.log('Blob created, size:', blob.size, 'type:', blob.type);
      
      if (blob.size === 0) {
        throw new Error('Received empty file');
      }

      return window.URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error getting CV viewing URL:', error);
      throw error;
    }
  }

  // Helper method to get category display name
  getCategoryDisplayName(category) {
    const categoryNames = {
      'engineering': 'Engineering',
      'product': 'Product',
      'design': 'Design',
      'marketing': 'Marketing',
      'sales': 'Sales',
      'data': 'Data & Analytics',
      'leadership': 'Leadership',
      'operations': 'Operations',
      'other': 'Other'
    };
    return categoryNames[category] || category;
  }

  // Helper method to get category icon
  getCategoryIcon(category) {
    const categoryIcons = {
      'engineering': '⚙️',
      'product': '📱',
      'design': '🎨',
      'marketing': '📢',
      'sales': '💼',
      'data': '📊',
      'leadership': '👔',
      'operations': '🔧',
      'other': '📄'
    };
    return categoryIcons[category] || '📄';
  }

  // Helper method to format file size
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Helper method to get confidence score color
  getConfidenceScoreColor(score) {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  }

  // Helper method to get processing status display
  getProcessingStatusDisplay(status) {
    const statusDisplay = {
      'pending': { text: 'Pending Analysis', color: 'text-gray-500', icon: '⏳' },
      'analyzing': { text: 'Analyzing...', color: 'text-blue-500', icon: '🔄' },
      'completed': { text: 'Analysis Complete', color: 'text-green-500', icon: '✅' },
      'failed': { text: 'Analysis Failed', color: 'text-red-500', icon: '❌' }
    };
    return statusDisplay[status] || statusDisplay['pending'];
  }
}

export const cvService = new CVService(); 